import { type ReactNode } from 'react'
import {
  type MetaFunction,
  type ActionFunctionArgs,
  redirect,
  data,
  Form,
  Link,
  useLoaderData,
} from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { unEnroll } from '#app/controls/enrollment.ts'
import {
  createEnrollmentForUser,
  createUser,
  deleteAllUsers,
  deleteUser,
  signIn,
  updateUser,
} from '#app/controls/users.ts'
import { type AccessCode } from '#app/types/access-code.ts'
import { type OrganizationWithAccessCodes } from '#app/types/organization.ts'
import { getOrganizationList } from '#app/utils/admin-api.server.ts'
import { sessionKey } from '#app/utils/auth.server.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { requireControlsToBeEnabled } from '#app/utils/controls.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { type LocalizedContent } from '#app/utils/i18n.ts'
import { cn, invariantResponse } from '#app/utils/misc.tsx'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { Status } from '#app/utils/status.ts'

interface TrainingProgram {
  _id: string
  title: LocalizedContent
}

export async function loader() {
  requireControlsToBeEnabled()

  const users = await prisma.user.findMany({
    select: {
      id: true,
      email: true,
      firstName: true,
      lastName: true,
      organizationId: true,
      enrollments: true,
      lessonsOnboardingCompleted: true,
    },
  })

  const trainingProgramQuery = `*[_type == 'trainingProgram']{_id, title}`
  const trainingProgramResult = await executeQuery(trainingProgramQuery)

  if (trainingProgramResult.status !== Status.Success) {
    throw data({}, { status: 500 })
  }

  const trainingPrograms = trainingProgramResult.data as TrainingProgram[]

  const trainingProgramsById: Record<string, TrainingProgram> = {}
  trainingPrograms.forEach(
    trainingProgram =>
      (trainingProgramsById[trainingProgram._id] = trainingProgram),
  )

  const organizationListResult = await getOrganizationList()
  if (organizationListResult.status !== Status.Success) {
    throw data({}, { status: 500 })
  }

  const organizations =
    organizationListResult.data as OrganizationWithAccessCodes[]

  const organizationsById: Record<string, OrganizationWithAccessCodes> = {}
  organizations.forEach(
    organization => (organizationsById[organization.id] = organization),
  )

  let defaultOrganization: OrganizationWithAccessCodes | undefined
  let defaultAccessCode: AccessCode | undefined

  for (const organization of organizations) {
    if (organization.accessCodes.length > 0) {
      defaultOrganization = organization
      defaultAccessCode = organization.accessCodes[0]
      break
    }
  }

  return {
    users,
    trainingPrograms: trainingProgramsById,
    organizations: organizationsById,
    defaultOrganization,
    defaultAccessCode,
  }
}

const INTENT_SIGN_IN = 'sign-in'
const INTENT_DELETE = 'delete'
const INTENT_TOGGLE_ONBOARDING_COMPLETION = 'toggle-onboarding-completion'
const INTENT_UNENROLL = 'unenroll'
const INTENT_RE_ENROLL = 'reEnroll'

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const intent = formData.get('intent')

  switch (intent) {
    case 'create': {
      const organizationId = formData.get('organizationId')
      const accessCodeId = formData.get('accessCodeId')
      const trainingProgramId = formData.get('trainingProgramId')
      invariantResponse(
        typeof organizationId === 'string',
        'organization id must be a string',
      )
      invariantResponse(
        typeof accessCodeId === 'string',
        'access code id must be a string',
      )
      invariantResponse(
        typeof trainingProgramId === 'string',
        'training program id must be a string',
      )
      const user = await createUser({
        organizationId,
        accessCodeId,
      })
      await createEnrollmentForUser(user.id, trainingProgramId)
      return redirect('/controls/users')
    }
    case 'deleteAll': {
      await deleteAllUsers()
      return redirect('/controls/users')
    }
    case INTENT_DELETE: {
      const userId = formData.get('userId')
      invariantResponse(typeof userId === 'string', 'userId must be a string')

      await deleteUser(userId)
      return redirect('/controls/users')
    }
    case INTENT_SIGN_IN: {
      const userId = formData.get('userId')
      invariantResponse(typeof userId === 'string', 'userId must be a string')

      const session = await signIn(userId)
      const authSession = await authSessionStorage.getSession(
        request.headers.get('cookie'),
      )
      authSession.set(sessionKey, session.id)

      return redirect('/controls/users', {
        headers: {
          'set-cookie': await authSessionStorage.commitSession(authSession, {
            expires: session.expirationDate,
          }),
        },
      })
    }
    case INTENT_TOGGLE_ONBOARDING_COMPLETION: {
      const userId = formData.get('userId')
      invariantResponse(typeof userId === 'string', 'userId must be a string')

      const lessonsOnboardingCompletedString = formData.get(
        'lessonsOnboardingCompleted',
      ) as string
      if (!lessonsOnboardingCompletedString) return { error: 500 }

      const lessonsOnboardingCompleted = parseInt(
        lessonsOnboardingCompletedString,
      )
      invariantResponse(
        typeof lessonsOnboardingCompleted === 'number',
        'lessonsOnboardingCompletedString must be a stringified number',
      )

      await updateUser(userId, {
        lessonsOnboardingCompleted: !lessonsOnboardingCompleted,
      })
      return { success: true }
    }
    case INTENT_UNENROLL: {
      const enrollmentId = formData.get('enrollmentId')
      invariantResponse(
        typeof enrollmentId === 'string',
        'enrollmentId must be a string',
      )

      await unEnroll(enrollmentId)
      return redirect('/controls/users')
    }
    case INTENT_RE_ENROLL: {
      const enrollmentId = formData.get('enrollmentId')
      invariantResponse(
        typeof enrollmentId === 'string',
        'enrollmentId must be a string',
      )

      await prisma.enrollment.update({
        where: { id: enrollmentId },
        data: {
          expiresAt: new Date(
            Date.now() + 1000 * 60 * 60 * 24 * 366, // 1 year + 1 day day from now
          ),
        },
      })
      return redirect('/controls/users')
    }
    default: {
      throw new Response(`${intent} intent is not supported`, {
        status: 400,
      })
    }
  }
}

export default function UsersControlRoute() {
  const data = useLoaderData<typeof loader>()
  const {
    users,
    trainingPrograms,
    organizations,
    defaultOrganization,
    defaultAccessCode,
  } = data

  const canCreateUser =
    defaultOrganization !== undefined && defaultAccessCode !== undefined

  const ActionButton = ({
    intent,
    destructive = false,
    children,
  }: {
    intent: string
    destructive?: boolean
    children: ReactNode
  }) => {
    const hoverColor = !!destructive
      ? 'hover:text-error-4'
      : 'hover:text-cyan-11'
    return (
      <Button
        className={cn('h-auto p-0', hoverColor)}
        variant="link"
        type="submit"
        name="intent"
        value={intent}
      >
        {children}
      </Button>
    )
  }

  return (
    <>
      <ul className="flex gap-3">
        <li>
          <Link to={'/controls'}>Controls</Link>
        </li>
        <li className="flex items-center gap-3">
          ▶️
          <span className="inline-flex items-center gap-1.5">Users</span>
        </li>
      </ul>
      {!defaultOrganization && (
        <div className="text-error-4 my-6">
          Error: Your app is not set up with any organizations. In order to
          create specific or generic users, please first create an organization
          with the admin app. New users are created with an associated access
          code, and that cannot happen without an organization they can be
          associated with.
        </div>
      )}
      {defaultOrganization && !defaultAccessCode && (
        <div className="text-error-4 my-6">
          Error: Your default organization, {defaultOrganization?.name}, does
          not have any training programs associated with it. In order to create
          specific or generic users, you'll need to add a training program to
          the organization in the admin app.
        </div>
      )}
      <div className="my-6">
        <Form method="POST">
          {canCreateUser && <Link to="create">Create specific user</Link>}

          <input
            type="hidden"
            name="organizationId"
            value={defaultOrganization?.id}
          />

          <input
            type="hidden"
            name="accessCodeId"
            value={defaultAccessCode?.id}
          />

          <input
            type="hidden"
            name="trainingProgramId"
            value={defaultAccessCode?.trainingProgramId}
          />

          <Button
            disabled={!canCreateUser}
            variant="link"
            type="submit"
            name="intent"
            value="create"
          >
            Create generic user
          </Button>

          <Button variant="link" type="submit" name="intent" value="deleteAll">
            Delete all
          </Button>
        </Form>
      </div>
      <table className="my-6 w-full table-auto border-collapse border">
        <thead>
          <tr>
            <th className="border p-2">ID</th>
            <th className="border p-2">Email</th>
            <th className="border p-2">First name</th>
            <th className="border p-2">Last name</th>
            <th className="border p-2">Training Program</th>
            <th className="border p-2">Organization</th>
            <th className="border p-2">Lessons Onboarding</th>
            <th className="border p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td className="border p-2">{user.id}</td>
              <td className="border p-2">{user.email}</td>
              <td className="border p-2">{user.firstName}</td>
              <td className="border p-2">{user.lastName}</td>
              <td className="border p-2">
                {user.enrollments.map(enrollment => (
                  <Enrollment
                    key={enrollment.id}
                    enrollment={enrollment}
                    trainingProgramTitle={
                      trainingPrograms[enrollment.trainingProgramId]?.title
                        .en || ''
                    }
                  />
                ))}
              </td>
              <td className="border p-2">
                {user.organizationId
                  ? organizations[user.organizationId]?.name
                  : ''}
              </td>
              <td className="border p-2">
                {user.lessonsOnboardingCompleted ? 'Completed' : 'Incomplete'}
              </td>
              <td className="border">
                <Form method="POST" className="flex flex-col gap-3 py-2">
                  <input type="hidden" name="userId" value={user.id} />
                  <input
                    type="hidden"
                    name="lessonsOnboardingCompleted"
                    value={user.lessonsOnboardingCompleted ? 1 : 0}
                  />

                  <ActionButton intent={INTENT_SIGN_IN}>Sign in</ActionButton>
                  <Link to={`/controls/users/${user.id}/create-enrollment`}>
                    Create enrollment
                  </Link>
                  <ActionButton intent={INTENT_TOGGLE_ONBOARDING_COMPLETION}>
                    Toggle Onboarding
                  </ActionButton>
                  <ActionButton destructive intent={INTENT_DELETE}>
                    Delete
                  </ActionButton>
                </Form>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: `Controls > Users | ${siteTitle}` }]
}

type EnrollmentProps = {
  enrollment: {
    id: string
    createdAt: Date
    userId: string
    trainingProgramId: string
    expiresAt: Date
  }
  trainingProgramTitle: string
}

function Enrollment({ enrollment, trainingProgramTitle }: EnrollmentProps) {
  const expired = enrollment.expiresAt < new Date()
  return (
    <div key={enrollment.id}>
      {trainingProgramTitle}
      <div className="text-1 text-gray-600">
        Enrolled: {enrollment.createdAt.toLocaleDateString()}
      </div>
      <div className="text-1 text-gray-600">
        Expires: {enrollment.expiresAt.toLocaleDateString()}
      </div>
      {!expired && (
        <Form method="post" className="inline">
          <input type="hidden" name="enrollmentId" value={enrollment.id} />
          <input type="hidden" name="intent" value={INTENT_UNENROLL} />
          <button
            type="submit"
            className="text-cyan-11 link-underline text-1 mt-1 cursor-pointer"
          >
            ❌ Unenroll
          </button>
        </Form>
      )}
      {expired && (
        <Form method="post" className="inline">
          <input type="hidden" name="enrollmentId" value={enrollment.id} />
          <input type="hidden" name="intent" value={INTENT_RE_ENROLL} />
          <button
            type="submit"
            className="text-cyan-11 link-underline text-1 mt-1 cursor-pointer"
          >
            🔄 Re-enroll
          </button>
        </Form>
      )}
    </div>
  )
}
